import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { Button } from "@/components/_components/button";
import { cn } from "@/lib/utils";
import { inputStyles } from "theme/components/input";
import { textareaStyles } from "theme/components/textarea";
import { buttonStyles } from "theme/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { Checkbox } from "@/components/_components/checkbox";
import { ChevronLeft, Save, FileText } from "lucide-react";
import { toast } from "react-hot-toast";
import { MOCK_CANDIDATE_DATA } from "@/constants/mock-data";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";
import { isAxiosError } from "axios";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";

interface CandidateData {
  id: number;
  fullName: string;
  email: string;
  mobile: string;
  phone: string;
  totalExperience: string;
  relevantExperience: string;
  resumeLastUpdated: string;
  gender: string;
  jobStabilityRating: number;
  jobChangeInLast5Years: boolean;
  blackListed: boolean;
  maxTravelDistance: number;
  dateAvailable: string;
  noticePeriod: string;
  onboardingStatus: string;
  preferredLanguage: string;
  title: string;
  about: string;
  reasonForChange: string;
  completenessPercentage: number;
  // JSON fields as strings for textarea input
  agency: string;
  doNotContact: string;
  contactPreferences: string;
  jobboardSource: string;
  jobPreference: string;
  profileCompleteness: string;
  skills: string;
  employmentHistory: string;
  educations: string;
  certifications: string;
  socialLinks: string;
  languages: string;
  address: string;
  preferredLocations: string;
}

const CreateCandidatePage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_TESTING_UPDATE">
      <CreateCandidateContent />
    </PageAccessGuard>
  );
};

const CreateCandidateContent: React.FC = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  // Initialize with mock data
  const [formData, setFormData] = useState<CandidateData>({
    id: 0,
    fullName: MOCK_CANDIDATE_DATA.fullName,
    email: MOCK_CANDIDATE_DATA.email,
    mobile: MOCK_CANDIDATE_DATA.mobile,
    phone: MOCK_CANDIDATE_DATA.phone,
    totalExperience: MOCK_CANDIDATE_DATA.totalExperience,
    relevantExperience: MOCK_CANDIDATE_DATA.relevantExperience,
    resumeLastUpdated: MOCK_CANDIDATE_DATA.resumeLastUpdated,
    gender: MOCK_CANDIDATE_DATA.gender,
    jobStabilityRating: MOCK_CANDIDATE_DATA.jobStabilityRating,
    jobChangeInLast5Years: MOCK_CANDIDATE_DATA.jobChangeInLast5Years,
    blackListed: MOCK_CANDIDATE_DATA.blackListed,
    maxTravelDistance: MOCK_CANDIDATE_DATA.maxTravelDistance,
    dateAvailable: MOCK_CANDIDATE_DATA.dateAvailable,
    noticePeriod: MOCK_CANDIDATE_DATA.noticePeriod,
    onboardingStatus: MOCK_CANDIDATE_DATA.onboardingStatus,
    preferredLanguage: MOCK_CANDIDATE_DATA.preferredLanguage,
    title: MOCK_CANDIDATE_DATA.title,
    about: MOCK_CANDIDATE_DATA.about,
    reasonForChange: MOCK_CANDIDATE_DATA.reasonForChange,
    completenessPercentage: MOCK_CANDIDATE_DATA.completenessPercentage,
    agency: JSON.stringify(MOCK_CANDIDATE_DATA.agency, null, 2),
    doNotContact: JSON.stringify(MOCK_CANDIDATE_DATA.doNotContact, null, 2),
    contactPreferences: JSON.stringify(MOCK_CANDIDATE_DATA.contactPreferences, null, 2),
    jobboardSource: JSON.stringify(MOCK_CANDIDATE_DATA.jobboardSource, null, 2),
    jobPreference: JSON.stringify(MOCK_CANDIDATE_DATA.jobPreference, null, 2),
    profileCompleteness: JSON.stringify(MOCK_CANDIDATE_DATA.profileCompleteness, null, 2),
    skills: JSON.stringify(MOCK_CANDIDATE_DATA.skills, null, 2),
    employmentHistory: JSON.stringify(MOCK_CANDIDATE_DATA.employmentHistory, null, 2),
    educations: JSON.stringify(MOCK_CANDIDATE_DATA.educations, null, 2),
    certifications: JSON.stringify(MOCK_CANDIDATE_DATA.certifications, null, 2),
    socialLinks: JSON.stringify(MOCK_CANDIDATE_DATA.socialLinks, null, 2),
    languages: JSON.stringify(MOCK_CANDIDATE_DATA.languages, null, 2),
    address: JSON.stringify(MOCK_CANDIDATE_DATA.address, null, 2),
    preferredLocations: JSON.stringify(MOCK_CANDIDATE_DATA.preferredLocations, null, 2)
  });

  const handleInputChange = (field: keyof CandidateData, value: string | number | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);

      // Parse JSON fields
      const jsonFields = [
        'agency', 'doNotContact', 'contactPreferences', 'jobboardSource',
        'jobPreference', 'profileCompleteness', 'skills', 'employmentHistory',
        'educations', 'certifications', 'socialLinks', 'languages', 'address', 'preferredLocations'
      ];

      const submitData: any = { ...formData };

      for (const field of jsonFields) {
        try {
          submitData[field] = JSON.parse(formData[field as keyof CandidateData] as string);
        } catch (error) {
          toast.error(`Invalid JSON in ${field} field`);
          return;
        }
      }

      await customInstance({
        url: `${API_CONFIG.ENDPOINTS.TEMP_CANDIDATE}/id/0`,
        method: 'PUT',
        data: submitData
      });

      toast.success("Candidate created successfully!");
      navigate(ROUTES.TESTING_CANDIDATE_MGMT);
    } catch (error) {
      console.error('Error creating candidate:', error);

      // Extract error message from API response
      let errorMessage = "Failed to create candidate";
      if (isAxiosError(error)) {
        const responseData = error.response?.data as { message?: string; error?: string } | undefined;
        if (responseData?.message) {
          errorMessage = `Failed to create candidate: ${responseData.message}`;
        } else if (responseData?.error) {
          errorMessage = `Failed to create candidate: ${responseData.error}`;
        } else if (error.message) {
          errorMessage = `Failed to create candidate: ${error.message}`;
        }
      } else if (error instanceof Error) {
        errorMessage = `Failed to create candidate: ${error.message}`;
      }

      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(ROUTES.TESTING_CANDIDATE_MGMT)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Candidates
        </Button>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Add Candidate</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFormData({
                  id: 0,
                  fullName: MOCK_CANDIDATE_DATA.fullName,
                  email: MOCK_CANDIDATE_DATA.email,
                  mobile: MOCK_CANDIDATE_DATA.mobile,
                  phone: MOCK_CANDIDATE_DATA.phone,
                  totalExperience: MOCK_CANDIDATE_DATA.totalExperience,
                  relevantExperience: MOCK_CANDIDATE_DATA.relevantExperience,
                  resumeLastUpdated: MOCK_CANDIDATE_DATA.resumeLastUpdated,
                  gender: MOCK_CANDIDATE_DATA.gender,
                  jobStabilityRating: MOCK_CANDIDATE_DATA.jobStabilityRating,
                  jobChangeInLast5Years: MOCK_CANDIDATE_DATA.jobChangeInLast5Years,
                  blackListed: MOCK_CANDIDATE_DATA.blackListed,
                  maxTravelDistance: MOCK_CANDIDATE_DATA.maxTravelDistance,
                  dateAvailable: MOCK_CANDIDATE_DATA.dateAvailable,
                  noticePeriod: MOCK_CANDIDATE_DATA.noticePeriod,
                  onboardingStatus: MOCK_CANDIDATE_DATA.onboardingStatus,
                  preferredLanguage: MOCK_CANDIDATE_DATA.preferredLanguage,
                  title: MOCK_CANDIDATE_DATA.title,
                  about: MOCK_CANDIDATE_DATA.about,
                  reasonForChange: MOCK_CANDIDATE_DATA.reasonForChange,
                  completenessPercentage: MOCK_CANDIDATE_DATA.completenessPercentage,
                  agency: JSON.stringify(MOCK_CANDIDATE_DATA.agency, null, 2),
                  doNotContact: JSON.stringify(MOCK_CANDIDATE_DATA.doNotContact, null, 2),
                  contactPreferences: JSON.stringify(MOCK_CANDIDATE_DATA.contactPreferences, null, 2),
                  jobboardSource: JSON.stringify(MOCK_CANDIDATE_DATA.jobboardSource, null, 2),
                  jobPreference: JSON.stringify(MOCK_CANDIDATE_DATA.jobPreference, null, 2),
                  profileCompleteness: JSON.stringify(MOCK_CANDIDATE_DATA.profileCompleteness, null, 2),
                  skills: JSON.stringify(MOCK_CANDIDATE_DATA.skills, null, 2),
                  employmentHistory: JSON.stringify(MOCK_CANDIDATE_DATA.employmentHistory, null, 2),
                  educations: JSON.stringify(MOCK_CANDIDATE_DATA.educations, null, 2),
                  certifications: JSON.stringify(MOCK_CANDIDATE_DATA.certifications, null, 2),
                  socialLinks: JSON.stringify(MOCK_CANDIDATE_DATA.socialLinks, null, 2),
                  languages: JSON.stringify(MOCK_CANDIDATE_DATA.languages, null, 2),
                  address: JSON.stringify(MOCK_CANDIDATE_DATA.address, null, 2),
                  preferredLocations: JSON.stringify(MOCK_CANDIDATE_DATA.preferredLocations, null, 2)
                });
                toast.success("Mock data loaded successfully!");
              }}
              className={cn(buttonStyles, "text-blue-600 border-blue-600 hover:bg-blue-50 border")}
            >
              <FileText className="h-4 w-4 mr-2" />
              Load Mock Data
            </button>
            <Button
              onClick={() => {
                setFormData({
                  id: 0,
                  fullName: "",
                  email: "",
                  mobile: "",
                  phone: "",
                  totalExperience: "",
                  relevantExperience: "",
                  resumeLastUpdated: "",
                  gender: "",
                  jobStabilityRating: 0,
                  jobChangeInLast5Years: false,
                  blackListed: false,
                  maxTravelDistance: 0,
                  dateAvailable: "",
                  noticePeriod: "",
                  onboardingStatus: "",
                  preferredLanguage: "",
                  title: "",
                  about: "",
                  reasonForChange: "",
                  completenessPercentage: 0,
                  agency: "",
                  doNotContact: "",
                  contactPreferences: "",
                  jobboardSource: "",
                  jobPreference: "",
                  profileCompleteness: "",
                  skills: "",
                  employmentHistory: "",
                  educations: "",
                  certifications: "",
                  socialLinks: "",
                  languages: "",
                  address: "",
                  preferredLocations: ""
                });
                toast.success("Form cleared successfully!");
              }}
              variant="outline"
              className="text-gray-600 border-gray-300 hover:bg-gray-50"
            >
              Clear Form
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSaving}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? "Saving..." : "Save Candidate"}
            </Button>
          </div>
        </div>

        {/* Personal Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="fullName">Full Name *</label>
                <input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Enter full name"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="title">Job Title *</label>
                <input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter job title"
                  className={inputStyles}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label htmlFor="mobile">Mobile Phone *</label>
                <input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  placeholder="Enter mobile number"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="gender">Gender</label>
                <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="preferredLanguage">Preferred Language</label>
                <Select value={formData.preferredLanguage} onValueChange={(value) => handleInputChange('preferredLanguage', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Spanish">Spanish</SelectItem>
                    <SelectItem value="French">French</SelectItem>
                    <SelectItem value="German">German</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="resumeLastUpdated">Resume Last Updated</label>
                <input
                  id="resumeLastUpdated"
                  type="date"
                  value={formData.resumeLastUpdated}
                  onChange={(e) => handleInputChange('resumeLastUpdated', e.target.value)}
                  className={inputStyles}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Information Section */}
        <Card>
          <CardHeader>
            <CardTitle>Professional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="totalExperience">Total Experience (Years)</label>
                <input
                  id="totalExperience"
                  value={formData.totalExperience}
                  onChange={(e) => handleInputChange('totalExperience', e.target.value)}
                  placeholder="e.g., 5"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="relevantExperience">Relevant Experience (Years)</label>
                <input
                  id="relevantExperience"
                  value={formData.relevantExperience}
                  onChange={(e) => handleInputChange('relevantExperience', e.target.value)}
                  placeholder="e.g., 3"
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="noticePeriod">Notice Period</label>
                <Select value={formData.noticePeriod} onValueChange={(value) => handleInputChange('noticePeriod', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select notice period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Immediate">Immediate</SelectItem>
                    <SelectItem value="1 Week">1 Week</SelectItem>
                    <SelectItem value="2 Weeks">2 Weeks</SelectItem>
                    <SelectItem value="1 Month">1 Month</SelectItem>
                    <SelectItem value="2 Months">2 Months</SelectItem>
                    <SelectItem value="3 Months">3 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="jobStabilityRating">Job Stability Rating (1-10)</label>
                <input
                  id="jobStabilityRating"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.jobStabilityRating}
                  onChange={(e) => handleInputChange('jobStabilityRating', Number.parseInt(e.target.value) || 0)}
                  className={inputStyles}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dateAvailable">Date Available</label>
                <input
                  id="dateAvailable"
                  type="date"
                  value={formData.dateAvailable ? formData.dateAvailable.split('T')[0] : ''}
                  onChange={(e) => handleInputChange('dateAvailable', e.target.value ? `${e.target.value}T00:00:00.000Z` : '')}
                  className={inputStyles}
                />
              </div>
              <div>
                <label htmlFor="maxTravelDistance">Max Travel Distance (miles)</label>
                <input
                  id="maxTravelDistance"
                  type="number"
                  value={formData.maxTravelDistance}
                  onChange={(e) => handleInputChange('maxTravelDistance', Number.parseInt(e.target.value) || 0)}
                  placeholder="e.g., 25"
                  className={inputStyles}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="jobChangeInLast5Years"
                  checked={formData.jobChangeInLast5Years}
                  onCheckedChange={(checked) => handleInputChange('jobChangeInLast5Years', checked as boolean)}
                />
                <label htmlFor="jobChangeInLast5Years">Job change in last 5 years</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="blackListed"
                  checked={formData.blackListed}
                  onCheckedChange={(checked) => handleInputChange('blackListed', checked as boolean)}
                />
                <label htmlFor="blackListed">Blacklisted</label>
              </div>
            </div>

            <div>
              <label htmlFor="about">About / Summary</label>
              <textarea
                id="about"
                value={formData.about}
                onChange={(e) => handleInputChange('about', e.target.value)}
                className={cn(textareaStyles, "min-h-[120px]")}
                placeholder="Brief professional summary..."
              />
            </div>

            <div>
              <label htmlFor="reasonForChange">Reason for Job Change</label>
              <textarea
                id="reasonForChange"
                value={formData.reasonForChange}
                onChange={(e) => handleInputChange('reasonForChange', e.target.value)}
                className={cn(textareaStyles, "min-h-[100px]")}
                placeholder="Why are you looking for a new opportunity?"
              />
            </div>
          </CardContent>
        </Card>

        {/* Structured Data Section */}
        <Card>
          <CardHeader>
            <CardTitle>Structured Information</CardTitle>
            <p className="text-sm text-gray-600">The following fields contain structured data in JSON format</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="agency">Agency Information</label>
                <textarea
                  id="agency"
                  value={formData.agency}
                  onChange={(e) => handleInputChange('agency', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"name": "Agency Name", "sfdcId": "ID"}'
                />
              </div>
              <div>
                <label htmlFor="jobboardSource">Jobboard Source</label>
                <textarea
                  id="jobboardSource"
                  value={formData.jobboardSource}
                  onChange={(e) => handleInputChange('jobboardSource', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"jobboardName": "Portal Name", "sfdcId": "ID"}'
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactPreferences">Contact Preferences</label>
                <textarea
                  id="contactPreferences"
                  value={formData.contactPreferences}
                  onChange={(e) => handleInputChange('contactPreferences', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"email": true, "phone": true, "sms": true, "whatsapp": true}'
                />
              </div>
              <div>
                <label htmlFor="doNotContact">Do Not Contact</label>
                <textarea
                  id="doNotContact"
                  value={formData.doNotContact}
                  onChange={(e) => handleInputChange('doNotContact', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='{"email": false, "phone": false, "sms": false, "whatsapp": false}'
                />
              </div>
            </div>

            <div>
              <label htmlFor="address">Address</label>
              <textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                placeholder='{"country": "USA", "state": "NY", "city": "New York", "zip": "10001"}'
              />
            </div>

            <div>
              <label htmlFor="jobPreference">Job Preferences</label>
              <textarea
                id="jobPreference"
                value={formData.jobPreference}
                onChange={(e) => handleInputChange('jobPreference', e.target.value)}
                className={cn(textareaStyles, "min-h-[180px] font-mono text-sm")}
                placeholder='{"openToRelocate": false, "openToOnsite": false, "openToRemote": false, "titleMatchCriteria": ["Engineer", "Developer"]}'
              />
            </div>

            <div>
              <label htmlFor="skills">Skills</label>
              <textarea
                id="skills"
                value={formData.skills}
                onChange={(e) => handleInputChange('skills', e.target.value)}
                className={cn(textareaStyles, "min-h-[150px] font-mono text-sm")}
                placeholder='[{"title": "Python", "level": "3"}, {"title": "JavaScript", "level": "2"}]'
              />
            </div>

            <div>
              <label htmlFor="employmentHistory">Employment History</label>
              <textarea
                id="employmentHistory"
                value={formData.employmentHistory}
                onChange={(e) => handleInputChange('employmentHistory', e.target.value)}
                className={cn(textareaStyles, "min-h-[200px] font-mono text-sm")}
                placeholder='[{"employerName": "Company", "jobTitle": "Position", "startDate": "2020-01-01T00:00:00.000Z", "endDate": "2023-01-01T00:00:00.000Z", "currentlyWorking": false}]'
              />
            </div>

            <div>
              <label htmlFor="educations">Education</label>
              <textarea
                id="educations"
                value={formData.educations}
                onChange={(e) => handleInputChange('educations', e.target.value)}
                className={cn(textareaStyles, "min-h-[200px] font-mono text-sm")}
                placeholder='[{"degree": "Bachelor of Science", "institute": "University Name", "startDate": "2016-01-01T00:00:00.000Z", "endDate": "2020-01-01T00:00:00.000Z"}]'
              />
            </div>

            <div>
              <label htmlFor="certifications">Certifications</label>
              <textarea
                id="certifications"
                value={formData.certifications}
                onChange={(e) => handleInputChange('certifications', e.target.value)}
                className={cn(textareaStyles, "min-h-[150px] font-mono text-sm")}
                placeholder='[{"name": "AWS Certified", "issuingAuthority": "Amazon", "certificationNumber": "123456", "expirationDate": "2025-01-01T00:00:00.000Z"}]'
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="socialLinks">Social Links</label>
                <textarea
                  id="socialLinks"
                  value={formData.socialLinks}
                  onChange={(e) => handleInputChange('socialLinks', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='[{"name": "LinkedIn", "profileLink": "https://linkedin.com/in/username"}]'
                />
              </div>
              <div>
                <label htmlFor="languages">Languages</label>
                <textarea
                  id="languages"
                  value={formData.languages}
                  onChange={(e) => handleInputChange('languages', e.target.value)}
                  className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                  placeholder='[{"name": "English", "proficiency": "Fluent"}]'
                />
              </div>
            </div>

            <div>
              <label htmlFor="preferredLocations">Preferred Locations</label>
              <textarea
                id="preferredLocations"
                value={formData.preferredLocations}
                onChange={(e) => handleInputChange('preferredLocations', e.target.value)}
                className={cn(textareaStyles, "min-h-[100px] font-mono text-sm")}
                placeholder='["New York, NY, USA", "San Francisco, CA, USA"]'
              />
            </div>

            <div>
              <label htmlFor="profileCompleteness">Profile Completeness</label>
              <textarea
                id="profileCompleteness"
                value={formData.profileCompleteness}
                onChange={(e) => handleInputChange('profileCompleteness', e.target.value)}
                className={cn(textareaStyles, "min-h-[120px] font-mono text-sm")}
                placeholder='[{"category": "Personal Information", "weightage": 10, "isComplete": true}]'
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


export default CreateCandidatePage;
